/**
 * Espejo TS 1:1 del contrato de comandos de puente (`tools/puente-schemas.json`,
 * ADR 0003, refs loomitz/iris#24) — el "TERCER ANCLAJE" que el $comment del
 * schema anuncia:
 *
 *   schema JSON (fuente única) ─┬─ tipos.ts (este archivo)
 *                               └─ fixtures.ts (mensajes válidos/inválidos)
 *
 * El guard de drift (`web/src/tests/iris-puente-contrato.test.ts`) valida las
 * fixtures (tipadas con ESTE módulo) contra el schema con ajv y cruza las
 * listas `*_CONOCIDOS` de abajo contra los enums del schema. Si tipos.ts y el
 * schema divergen, revienta el test (o el type-check del build sobre las
 * fixtures tipadas).
 *
 * Convención: los `$defs` con `pattern` (Sitio, ComandoNombre, AnclaIris,
 * TourId, PaginaPath, OrigenExacto, SemverInformativo, IdCorrelacion) se
 * modelan como `string` — el pattern lo hace cumplir el schema/ajv, no el tipo
 * estructural. Los enums SÍ se cierran a uniones literales.
 */

// --- Constantes del sobre ---------------------------------------------------

/** `$defs/Proto` — discriminador de protocolo (fuera del wire versioning). */
export const PROTO = "iris-puente" as const
export type Proto = typeof PROTO

/** Major de protocolo v1. `$defs/VersionMajor` (integer ≥ 1). */
export const PUENTE_V = 1 as const

// --- Alias de string con pattern (validado por el schema) -------------------

/** `$defs/Sitio` — `^[a-z][a-z0-9-]*$`, ≤32. */
export type Sitio = string
/** `$defs/IdCorrelacion` — 1..64, de un solo uso (anti-replay). */
export type IdCorrelacion = string
/** `$defs/SemverInformativo` — `^\d+\.\d+\.\d+$` (telemetría, NO gatea). */
export type SemverInformativo = string
/** `$defs/ComandoNombre` — `^[a-z][a-z0-9_]*$`, ABIERTO (cierre en runtime). */
export type ComandoNombre = string
/** `$defs/CapacidadNombre` — token agrupado/versionado, p.ej. `carrito@1`. */
export type CapacidadNombre = string
/** `$defs/CodigoError` — `^[a-z][a-z0-9_]*$`, ABIERTO (US28 hacia el shell). */
export type CodigoError = string
/** `$defs/AnclaIris` — `^[a-z0-9]+(-[a-z0-9]+)*$` (guard anti-selector). */
export type AnclaIris = string
/** `$defs/TourId` — `^[a-z0-9-]+\.[a-z0-9][a-z0-9.-]*$` (prefijado por sitio). */
export type TourId = string
/** `$defs/PaginaPath` — pathname/template SOLO (`^/(?!/)[a-zA-Z0-9/_.:-]*$`). */
export type PaginaPath = string
/** `$defs/OrigenExacto` — origen https EXACTO (`^https://…$`). */
export type OrigenExacto = string

// --- Allowlists DOCUMENTALES (espejo de los enums del schema) ---------------

/**
 * `$defs/ComandosConocidos.enum`. El guard de drift asevera igualdad exacta
 * (ordenada) contra el schema; `tools/puente-comandos.mjs` lo espeja en el lado
 * del bridge (COMANDOS_V1) y el handler-map se construye EXACTAMENTE de aquí.
 */
export const COMANDOS_CONOCIDOS = [
  "cotizador_cargar_seleccion",
  "carrito_agregar_seleccion",
  "diagrama_abrir_modal",
  "tour_correr",
  "tour_avanzar",
  "tour_cancelar",
  "ancla_resaltar",
  "carrito_snapshot",
  "carrito_refrescar",
  "widget_presentar",
  "widget_ocultar",
] as const
export type ComandoConocido = (typeof COMANDOS_CONOCIDOS)[number]

/** Capacidades agrupadas/versionadas que el host anuncia en `hola_puente`. */
export const CAPACIDADES_CONOCIDAS = [
  "cotizador@1",
  "carrito@1",
  "diagrama@1",
  "tours@1",
  "widget@1",
] as const
export type CapacidadConocida = (typeof CAPACIDADES_CONOCIDAS)[number]

/** `$defs/CodigosConocidos.enum`. */
export const CODIGOS_ERROR_CONOCIDOS = [
  "no_permitido",
  "comando_desconocido",
  "payload_invalido",
  "version_incompatible",
  "apagado",
  "capacidad_no_configurada",
  "tour_desconocido",
  "tour_inactivo",
  "paso_invalido",
  "ancla_fuera_de_tour",
  "ancla_no_encontrada",
  "pagina_no_aplica",
  "error_interno",
] as const
export type CodigoErrorConocido = (typeof CODIGOS_ERROR_CONOCIDOS)[number]

/** `$defs/EventosConocidos.enum`. */
export const EVENTOS_CONOCIDOS = [
  "tour_evento",
  "presentacion_evento",
  "pagina_cambiada",
] as const
export type EventoConocido = (typeof EVENTOS_CONOCIDOS)[number]

/**
 * Llaves EXACTAS de `$defs/CartSnapshotItem.properties` — allowlist estricta de
 * print `src/lib/iris/cart-snapshot.ts`. El guard de drift la cruza contra el
 * schema; el proyector del bridge sólo copia estas llaves (fail-closed).
 */
export const LLAVES_CART_ITEM = [
  "tipo",
  "productKind",
  "rollo",
  "piezas",
  "medidas",
  "acabados",
  "madera",
  "canto",
  "barniz",
  "acabadoCanto",
  "opciones",
] as const
export type LlaveCartItem = (typeof LLAVES_CART_ITEM)[number]

// --- Snapshot del carrito (espejo de print cart-snapshot.ts) ----------------

/** `$defs/CartSnapshotMedidas`. */
export interface CartSnapshotMedidas {
  ancho: number
  largo: number
  unidad: string
}

/** `$defs/CartSnapshotAcabados` (solo papel; `margen` FUERA). */
export interface CartSnapshotAcabados {
  laminado?: string
  spray?: string
  montaje?: string
}

/** `$defs/CartSnapshotOpciones` (solo canvas). */
export interface CartSnapshotOpciones {
  bastidor?: boolean
  barniz?: boolean
}

/** `$defs/CartSnapshotItem`. */
export interface CartSnapshotItem {
  tipo: "papel" | "canvas"
  productKind?: string
  rollo?: string
  piezas?: number
  medidas?: CartSnapshotMedidas
  /** Solo papel. */
  acabados?: CartSnapshotAcabados
  /** Solo canvas. */
  madera?: string
  canto?: string
  barniz?: string
  acabadoCanto?: string
  opciones?: CartSnapshotOpciones
}

/** `$defs/CartSnapshot` (maxItems 20, minItems 1). */
export interface CartSnapshot {
  items: CartSnapshotItem[]
}

// --- Selección cotizable PII-safe ------------------------------------------

export interface SeleccionMedidas {
  ancho: number
  largo: number
  unidad: string
}

export interface SeleccionAcabados {
  laminado?: string | null
  spray?: string | null
  montaje?: string | null
}

export interface SeleccionMargen {
  valor: number
  unidad: string
  tipo: string
}

export interface SeleccionOpciones {
  bastidor?: boolean
  barniz?: boolean
}

export interface SeleccionCotizable {
  tipo: "papel" | "canvas"
  productKind: string
  rollo: string
  medidas: SeleccionMedidas
  piezas?: number
  acabados?: SeleccionAcabados | null
  margen?: SeleccionMargen | null
  madera?: string | null
  canto?: string | null
  barniz?: string | null
  acabadoCanto?: string | null
  opciones?: SeleccionOpciones | null
  originKey?: string | null
  originRouteName?: string | null
}

// --- Handshake --------------------------------------------------------------

/** `$defs/HolaShell` — shell → puente (anuncia versiones soportadas). */
export interface HolaShell {
  proto: Proto
  v: number
  tipo: "hola_shell"
  sitio: Sitio
  versiones: number[]
  shellVersion?: SemverInformativo
}

/** `$defs/HolaPuente` — puente → shell (capacidades; transfiere el MessagePort). */
export interface HolaPuente {
  proto: Proto
  v: number
  tipo: "hola_puente"
  sitio: Sitio
  version: number
  /** Allowlist del host ∩ implementados por este bridge. */
  comandos: ComandoNombre[]
  /** Capacidades agrupadas/versionadas soportadas por este host. */
  capacidades?: CapacidadNombre[]
  /** Ids vivos del catálogo local ([] = host sin tours, US22). */
  tours: TourId[]
  pagina: PaginaPath
  catalogoVersion: number
  catalogoDigest?: string
  bridgeVersion?: SemverInformativo
  /** Subset de `comandos` marcados para retiro (retiro real = major nuevo). */
  deprecados?: ComandoNombre[]
}

// --- Comandos (shell → puente) ---------------------------------------------

/** `$defs/TourCorrerPayload`. */
export interface TourCorrerPayload {
  tourId: TourId
  paso?: number
}
/** `$defs/TourAvanzarPayload`. */
export interface TourAvanzarPayload {
  tourId: TourId
  paso: number
}
/** `$defs/TourCancelarPayload`. */
export interface TourCancelarPayload {
  tourId: TourId
}
/** `$defs/AnclaResaltarPayload`. */
export interface AnclaResaltarPayload {
  tourId: TourId
  paso: number
  ancla: AnclaIris
}
/** `$defs/CarritoSnapshotPayload` (objeto vacío). */
export type CarritoSnapshotPayload = Record<string, never>
/** `$defs/CarritoRefrescarPayload` (objeto vacío). */
export type CarritoRefrescarPayload = Record<string, never>
/** `$defs/WidgetPresentarPayload` (solo presets, sin píxeles). */
export interface WidgetPresentarPayload {
  estado?: "abierto" | "cerrado"
  tamano?: "compacto" | "amplio"
}
/** `$defs/WidgetOcultarPayload` (sin parámetros). */
export type WidgetOcultarPayload = Record<string, never>
/** `$defs/CotizadorCargarSeleccionPayload`. */
export interface CotizadorCargarSeleccionPayload {
  seleccion: SeleccionCotizable
  abrir?: boolean
}
/** `$defs/CarritoAgregarSeleccionPayload`. */
export interface CarritoAgregarSeleccionPayload {
  seleccion: SeleccionCotizable
  piezas: number
}
/** `$defs/DiagramaAbrirModalPayload`. */
export interface DiagramaAbrirModalPayload {
  diagram: object
}

/** `$defs/Comando`. `comando` abierto; payload refinado por los conocidos. */
export interface Comando<P extends object = object> {
  proto: Proto
  v: number
  tipo: "comando"
  id: IdCorrelacion
  comando: ComandoNombre
  payload: P
}

// --- Respuestas (puente → shell) -------------------------------------------

/** `$defs/TourCorrerRespuesta`. */
export interface TourCorrerRespuesta {
  tourId: TourId
  paso: number
  pasosTotal: number
}
/** `$defs/TourAvanzarRespuesta`. */
export interface TourAvanzarRespuesta {
  tourId: TourId
  paso: number
  pasosTotal: number
  terminado: boolean
}
/** `$defs/TourCancelarRespuesta`. */
export interface TourCancelarRespuesta {
  tourId: TourId
}
/** `$defs/AnclaResaltarRespuesta`. */
export interface AnclaResaltarRespuesta {
  tourId: TourId
  paso: number
  ancla: AnclaIris
}
/** `$defs/CarritoSnapshotRespuesta` (cart:null EXPLÍCITO = vacío). */
export interface CarritoSnapshotRespuesta {
  cart: CartSnapshot | null
}
/** `$defs/CarritoRefrescarRespuesta`: el host terminó de reconciliar el carrito servido. */
export interface CarritoRefrescarRespuesta {
  actualizado: true
}
/** `$defs/WidgetPresentarRespuesta` (estado EFECTIVO). */
export interface WidgetPresentarRespuesta {
  estado: "abierto" | "cerrado"
  tamano: "compacto" | "amplio"
}
/** `$defs/WidgetOcultarRespuesta`. */
export interface WidgetOcultarRespuesta {
  oculto: true
}
export interface CotizadorCargarSeleccionRespuesta {
  aceptada: boolean
}
export interface CarritoAgregarSeleccionRespuesta {
  aceptada: boolean
}
export interface DiagramaAbrirModalRespuesta {
  abierto: boolean
}

/**
 * `$defs/ErrorPuente` — SIN texto libre (el schema prohíbe
 * mensaje/texto/html/detalleTexto). `detalle` = campos ESTRUCTURADOS tipados
 * por código (allowlist cerrada, `additionalProperties:false`).
 */
export interface ErrorPuenteDetalle {
  disponibles?: TourId[]
  paginasDelTour?: PaginaPath[]
  paginaActual?: PaginaPath
  tourId?: TourId
  paso?: number
  ancla?: AnclaIris
  comando?: ComandoNombre
}
export interface ErrorPuente {
  codigo: CodigoError
  detalle?: ErrorPuenteDetalle
}

/** `$defs/Respuesta` — ECOA `id` y `comando`; ok:true⇒payload, ok:false⇒error. */
export interface Respuesta<P extends object = object> {
  proto: Proto
  v: number
  tipo: "respuesta"
  id: IdCorrelacion
  comando: ComandoNombre
  ok: boolean
  payload?: P
  error?: ErrorPuente
}

// --- Eventos (puente → shell, sin correlación) ------------------------------

/** `$defs/TourEventoPayload`. */
export interface TourEventoPayload {
  tourId: TourId
  paso: number
  hito:
    | "paso_mostrado"
    | "paso_completado"
    | "tour_terminado"
    | "cancelado_por_usuario"
    | "ancla_no_encontrada"
}
/** `$defs/PresentacionEventoPayload`. */
export interface PresentacionEventoPayload {
  gesto: "abrir_solicitado" | "cerrar_solicitado"
}
/** `$defs/PaginaCambiadaPayload`. */
export interface PaginaCambiadaPayload {
  pagina: PaginaPath
}

/** `$defs/Evento` — `evento` ABIERTO (US28: desconocido = válido, se ignora). */
export interface Evento<P extends object = object> {
  proto: Proto
  v: number
  tipo: "evento"
  evento: string
  payload: P
}

/** `$defs/Envelope` — todo mensaje shell↔puente (oneOf cerrado por `tipo`). */
export type Envelope = HolaShell | HolaPuente | Comando | Respuesta | Evento

// --- Config del host / del sitio -------------------------------------------

/** `$defs/ConfigHost` — la declara el sitio host (vive en su repo). */
export interface ConfigHost {
  sitio: Sitio
  comandos?: ComandoNombre[]
  capacidades?: CapacidadNombre[]
  /** Path relativo MISMO ORIGEN del catálogo de tours (opcional). */
  toursUrl?: string
  carrito?: boolean
}

/** `$defs/ConfigSitio` — servida desde el dominio de Iris (fail-closed). */
export interface ConfigSitio {
  sitio: Sitio
  activo: boolean
  origenes: OrigenExacto[]
  comandos: ComandoNombre[]
  capacidades?: CapacidadNombre[]
}

// --- Tours / catálogos (autoría en el repo del host) ------------------------

/** `$defs/AccionEsperada`. */
export interface AccionEsperada {
  tipo: "click" | "input" | "navegacion" | "ninguna"
  pagina?: PaginaPath
}
/** `$defs/PasoTour` (`texto` autorado en el host, jamás viaja por el puente). */
export interface PasoTour {
  ancla: AnclaIris
  texto: string
  accionEsperada?: AccionEsperada
}
/** `$defs/Tour`. */
export interface Tour {
  id: TourId
  titulo: string
  descripcion: string
  version: number
  paginas?: PaginaPath[]
  pasos: PasoTour[]
}
/** `$defs/CatalogoTours`. */
export interface CatalogoTours {
  sitio: Sitio
  version: number
  tours: Tour[]
}

// --- Type guards del sobre por `tipo` ---------------------------------------

function esRegistro(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x)
}

/** ¿El objeto trae el sobre base correcto (proto+v)? */
export function esSobrePuente(x: unknown): x is Record<string, unknown> {
  return esRegistro(x) && x.proto === PROTO && typeof x.v === "number"
}

export function esComando(x: unknown): x is Comando {
  return (
    esSobrePuente(x) &&
    x.tipo === "comando" &&
    typeof x.id === "string" &&
    typeof x.comando === "string" &&
    esRegistro(x.payload)
  )
}

export function esHolaShell(x: unknown): x is HolaShell {
  return (
    esSobrePuente(x) &&
    x.tipo === "hola_shell" &&
    typeof x.sitio === "string" &&
    Array.isArray(x.versiones)
  )
}

export function esHolaPuente(x: unknown): x is HolaPuente {
  return (
    esSobrePuente(x) &&
    x.tipo === "hola_puente" &&
    typeof x.sitio === "string" &&
    typeof x.version === "number" &&
    Array.isArray(x.comandos) &&
    Array.isArray(x.tours) &&
    typeof x.pagina === "string"
  )
}

export function esRespuesta(x: unknown): x is Respuesta {
  return (
    esSobrePuente(x) &&
    x.tipo === "respuesta" &&
    typeof x.id === "string" &&
    typeof x.comando === "string" &&
    typeof x.ok === "boolean"
  )
}

export function esEvento(x: unknown): x is Evento {
  return (
    esSobrePuente(x) &&
    x.tipo === "evento" &&
    typeof x.evento === "string" &&
    esRegistro(x.payload)
  )
}
